import { api } from '@/shared/api';
import { unwrap, unwrapCollection } from '@/shared/api/envelope';

import {
    toClient,
    type Client,
    type ClientAddress,
    type ClientChanges,
    type NewClient,
} from './model';
import { clientResourceSchema } from './types';

/** Het adres onder de namen van de API; de API zet het in één schrijfwijze. */
function addressFields(address: ClientAddress) {
    return {
        address_street: address.street,
        address_number: address.number,
        address_suffix: address.suffix,
        address_postcode: address.postcode,
        address_city: address.city,
    };
}

export const clientApi = {
    /** `GET /clients?filter[search]=` - zoeken op naam, voor het formulier "Nieuwe keuring". */
    async search(term: string): Promise<Client[]> {
        return unwrapCollection(
            clientResourceSchema,
            await api.get('/clients', {
                query: { 'filter[search]': term.trim() || undefined, sort: 'name', per_page: 20 },
                cacheKey: false,
            }),
        ).items.map(toClient);
    },

    /**
     * `PATCH /clients/{client}` - naam, contact, e-mail, telefoon en adres van een
     * bestaande klant. Een 422 draagt de veldmeldingen onder de namen van de API
     * (`name`, `email`, `address_postcode`, …).
     */
    async update(clientUuid: string, changes: ClientChanges): Promise<Client> {
        const text = (value: string) => (value.trim() === '' ? null : value.trim());

        return toClient(
            unwrap(
                clientResourceSchema,
                await api.patch(
                    `/clients/${clientUuid}`,
                    {
                        name: changes.name.trim(),
                        contact_name: text(changes.contactName),
                        email: text(changes.email),
                        phone: text(changes.phone),
                        ...addressFields(changes.address),
                    },
                    { cacheKey: false },
                ),
            ),
        );
    },

    /**
     * `PATCH /clients/{client}` met alleen het adres: "Nieuwe keuring" legt het
     * huidige adres vast zonder aan naam of contact van de klant te komen.
     */
    async updateAddress(clientUuid: string, address: ClientAddress): Promise<Client> {
        return toClient(
            unwrap(
                clientResourceSchema,
                await api.patch(`/clients/${clientUuid}`, addressFields(address), {
                    cacheKey: false,
                }),
            ),
        );
    },

    /** `POST /clients` */
    async create(input: NewClient): Promise<Client> {
        return toClient(
            unwrap(
                clientResourceSchema,
                await api.post(
                    '/clients',
                    {
                        type: input.type,
                        name: input.name,
                        contact_name: input.contactName || null,
                        email: input.email || null,
                        phone: input.phone || null,
                        ...(input.address ? addressFields(input.address) : {}),
                    },
                    { cacheKey: false },
                ),
            ),
        );
    },
};
